import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import Box from "@mui/material/Box"
import { t } from "i18next"
import { getStyleFieldLabel } from "../../../../helpers/properties.helpers"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import { updateObjectLockAspectRationPropertyAction } from "../../../../store/slices/objects.slice"
import ExpandableSection from "../../../common/ExpandableSection"
import DimensionsPair from "../../../common/PropertyTools/DimensionsPair"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import Row from "../../../common/PropertyTools/PropertyRow"
import SelectInput from "../../../common/PropertyTools/SelectInput"
import { LockAspectRatio } from "./LockAspectRatio"
import Rotation from "./Rotation"
import SingleAlignment from "./SingleAlignment"
interface Props {
    object: AnySceneObjectT
    handleSetValue: (property: any, value: any) => void
    expanded?: boolean
}

const SizeAndPosition = ({ object, handleSetValue, expanded = true }: Props) => {
    const dispatch = useAppDispatch()
    const lockAspectRation = useAppSelector((state) => state.editor.value.settings.lockAspectRatio)
    const width: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "width"
    )
    const height: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "height"
    )

    const top: SimpleStyleT | undefined = object.styles.find((style) => style.property === "top")
    const left: SimpleStyleT | undefined = object.styles.find((style) => style.property === "left")
    const position: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "position"
    )
    const overflow: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "overflow"
    )

    const alignItems: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "alignItems"
    )

    const justifyContent: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "justifyContent"
    )

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSetValue(e.target.name, e.target.value)
    }
    const handleOnChangeWithHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
        // an object without a height (a line) has no aspect ratio to keep
        if (lockAspectRation && width && height) {
            let newWidth: number = 0
            let newHeight: number = 0
            if (e.target.name === "width") {
                newWidth = Number(e.target.value)
                newHeight = Number(
                    Number((newWidth * Number(height?.value)) / Number(width?.value)).toFixed(2)
                )
            } else if (e.target.name === "height") {
                newHeight = Number(e.target.value)
                newWidth = Number(
                    Number((newHeight * Number(width?.value)) / Number(height?.value)).toFixed(2)
                )
            }
            dispatch(
                updateObjectLockAspectRationPropertyAction({
                    object: object,
                    height: newHeight,
                    width: newWidth,
                })
            )
        } else {
            handleSetValue(e.target.name, e.target.value)
        }
    }
    const handleAlign = (property: string, value: number) => {
        handleSetValue(property, value)
    }

    if (!width && !height && !top && !left && !overflow && !alignItems && !justifyContent)
        return <></>

    const isLine = object.type === "line"
    // the lock only means something when there is a height to keep in proportion
    const isRatioLocked = Boolean(lockAspectRation && width && height)
    const hasRotation = !["video-panel", "ticker"].includes(object.type)
    // where the object sits in its parent's layout and whether it clips — a line has neither
    const hasLayout =
        !isLine && !["video-panel", "ticker"].includes(object.type) && (position || overflow)
    const inputType = (style: SimpleStyleT) => (style.input.type === "number" ? "number" : "text")

    return (
        <ExpandableSection
            expanded={expanded}
            title={t("editor:size&positionSettings.title")}
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    <Row>
                        <SingleAlignment object={object} handleAlign={handleAlign} />
                    </Row>

                    {(width || height) && (
                        <Row gutter={width && height && <LockAspectRatio size="small" />}>
                            <DimensionsPair
                                locked={isRatioLocked}
                                width={
                                    width && (
                                        <PrefixedInput
                                            name="width"
                                            value={width.value}
                                            label={getStyleFieldLabel(width)}
                                            tooltip="Width"
                                            handleOnChange={handleOnChangeWithHeight}
                                            min={0}
                                            type={inputType(width)}
                                        />
                                    )
                                }
                                height={
                                    height && (
                                        <PrefixedInput
                                            name="height"
                                            value={height.value}
                                            label={getStyleFieldLabel(height)}
                                            tooltip="Height"
                                            handleOnChange={handleOnChangeWithHeight}
                                            min={0}
                                            type={inputType(height)}
                                        />
                                    )
                                }
                            />
                        </Row>
                    )}

                    {(top || left) && (
                        <Row>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    {top && (
                                        <PrefixedInput
                                            name="top"
                                            value={top.value}
                                            label={getStyleFieldLabel(top)}
                                            tooltip="Top"
                                            handleOnChange={handleOnChange}
                                            type={inputType(top)}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {left && (
                                        <PrefixedInput
                                            name="left"
                                            value={left.value}
                                            label={getStyleFieldLabel(left)}
                                            tooltip="Left"
                                            handleOnChange={handleOnChange}
                                            type={inputType(left)}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Row>
                    )}

                    {hasRotation && (
                        <Row>
                            <Rotation object={object} />
                        </Row>
                    )}

                    {hasLayout && (
                        <Row>
                            <Box
                                display="flex"
                                gap={1}
                                // the theme gives every form control a 3px margin
                                sx={{ "& .MuiFormControl-root": { m: 0, width: "100%" } }}
                            >
                                <Box flex={1} minWidth={0}>
                                    {position && (
                                        <SelectInput
                                            styleProperty={position}
                                            handleOnChange={handleOnChange}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {overflow && (
                                        <SelectInput
                                            styleProperty={overflow}
                                            handleOnChange={handleOnChange}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Row>
                    )}
                </Box>
            }
        />
    )
}

export default SizeAndPosition
